/**
 * Per-Rx timeline assembly (GP-1177).
 *
 * Fetches and merges a patient's clinical-verification history, counseling
 * records, and Rx-tagged notes into one entry per prescription. Extracted from
 * RxTimelineTab so the same consolidated per-Rx object can be reused by the
 * Counseling History "Print Report" action (which otherwise only had the single
 * counseling row) — one source of truth for the merge, no duplicated logic
 * (see CLAUDE.md).
 *
 * Pure data module (no React/MUI) so it can be unit-tested and shared.
 */
import api from '../../services/api';
import counselingService from '../../services/counselingService';
import notesService from '../../services/notesService';

export interface CounselingRecord {
  id: string;
  rxNumber?: string;
  drugName?: string;
  din?: string;
  doctorName?: string;
  patientName?: string;
  orderId?: string;
  status: string;
  reason: string;
  counselingBy: string;
  method: string;
  checklist: Record<string, { checked: boolean; notes: string }>;
  comments?: string;
  declinedReason?: string;
  agentName?: string;
  agentRelationship?: string;
  dateCounselled?: string;
  createdBy?: string;
  completedBy?: string;
  createdAt: string;
  completedAt?: string;
}

export interface PatientNote {
  id: string;
  content: string;
  type?: string;
  tags?: string[];
  author: { name: string; email?: string };
  createdAt: string;
  relatedPrescriptionId?: string;
}

export interface RxTimelineEntry {
  rxNumber: string;
  medicationName: string;
  orderNumber?: string;
  orderId: string;
  verifiedAt?: string;
  verifiedBy?: string;
  daysSupply?: number;
  verifiedDaysSupply?: number;
  drugVerification?: any;
  counselingRecords: CounselingRecord[];
  notes: PatientNote[];
}

/**
 * Assemble one {@link RxTimelineEntry} per prescription for a patient by
 * merging clinical-verification history, counseling records, and Rx-tagged
 * notes — the exact data the RX History tab renders. Individual sources fail
 * soft (counseling/notes default to empty) so a single 500 never blanks the
 * whole timeline.
 */
export async function fetchRxTimelineEntries(patientId: string): Promise<RxTimelineEntry[]> {
  const [historyRes, patientCounseling, notesRes] = await Promise.all([
    api.get(`/api/patients/${patientId}/clinical-history`),
    counselingService.getRecordsByPatient(patientId).catch(() => []),
    notesService
      .fetchNotesForPanel({ entityType: 'patient', entityId: patientId, includeDeleted: false })
      .catch(() => []),
  ]);

  const rxMap = new Map<string, RxTimelineEntry>();
  const orders = historyRes.data?.orders || [];

  for (const order of orders) {
    if (!order.clinicalVerifiedAt && !order.metadata?.clinicalVerification) continue;

    const drugVerifications = order.metadata?.clinicalVerification?.drugVerifications || [];
    if (drugVerifications.length > 0) {
      for (const dv of drugVerifications) {
        const rxNum = dv.rxNumber || order.rxNumber;
        if (!rxMap.has(rxNum)) {
          rxMap.set(rxNum, {
            rxNumber: rxNum,
            medicationName: dv.medicationName || order.medicationName,
            orderNumber: order.orderNumber,
            orderId: order.id,
            verifiedAt: order.metadata?.clinicalVerification?.verifiedAt || order.clinicalVerifiedAt,
            verifiedBy: order.metadata?.clinicalVerification?.verifiedBy || order.clinicalVerifiedBy,
            daysSupply: dv.daysSupply || order.daysSupply,
            verifiedDaysSupply: dv.verifiedDaysSupply || order.pharmacistVerifiedDaysSupply,
            drugVerification: dv,
            counselingRecords: [],
            notes: [],
          });
        }
      }
    } else {
      const rxNum = order.rxNumber;
      if (rxNum && !rxMap.has(rxNum)) {
        rxMap.set(rxNum, {
          rxNumber: rxNum,
          medicationName: order.medicationName,
          orderNumber: order.orderNumber,
          orderId: order.id,
          verifiedAt: order.clinicalVerifiedAt,
          verifiedBy: order.clinicalVerifiedBy,
          daysSupply: order.daysSupply,
          verifiedDaysSupply: order.pharmacistVerifiedDaysSupply,
          drugVerification: null,
          counselingRecords: [],
          notes: [],
        });
      }
    }
  }

  // Extract pharmacistNotes from clinical verification metadata (historical)
  for (const order of orders) {
    const cv = order.metadata?.clinicalVerification;
    const cvPharmacistNotes = cv?.pharmacistNotes;
    if (cvPharmacistNotes && typeof cvPharmacistNotes === 'string' && cvPharmacistNotes.trim()) {
      const dvs = cv?.drugVerifications || [];
      const rxNumbers: string[] = dvs.length > 0
        ? dvs.map((dv: any) => dv.rxNumber || order.rxNumber).filter(Boolean)
        : [order.rxNumber].filter(Boolean);
      const uniqueRxNumbers = [...new Set(rxNumbers)];
      for (const rxNum of uniqueRxNumbers) {
        if (rxMap.has(rxNum)) {
          rxMap.get(rxNum)!.notes.push({
            id: `cv-note-${order.id}-${rxNum}`,
            content: cvPharmacistNotes,
            type: 'clinical_verification',
            tags: [`rx:${rxNum}`, 'source:clinical-verification'],
            author: { name: cv?.verifiedBy || order.clinicalVerifiedBy || 'Pharmacist' },
            createdAt: cv?.verifiedAt || order.clinicalVerifiedAt || order.createdAt,
          });
        }
      }
    }
  }

  const counselingRecords = (patientCounseling || []) as unknown as CounselingRecord[];
  for (const record of counselingRecords) {
    if (!record.rxNumber) continue;
    if (rxMap.has(record.rxNumber)) {
      rxMap.get(record.rxNumber)!.counselingRecords.push(record);
    } else {
      // Counseling record with no matching clinical verification — create standalone entry
      rxMap.set(record.rxNumber, {
        rxNumber: record.rxNumber,
        medicationName: record.drugName || 'Unknown',
        orderNumber: undefined,
        orderId: '',
        verifiedAt: undefined,
        verifiedBy: undefined,
        daysSupply: undefined,
        verifiedDaysSupply: undefined,
        drugVerification: null,
        counselingRecords: [record],
        notes: [],
      });
    }
  }

  const allNotes: PatientNote[] = Array.isArray(notesRes) ? notesRes : [];
  for (const note of allNotes) {
    const rxTag = note.tags?.find((t: string) => t.startsWith('rx:'))?.replace('rx:', '');
    const rxNum = note.relatedPrescriptionId || rxTag;
    if (rxNum && rxMap.has(rxNum)) {
      rxMap.get(rxNum)!.notes.push(note);
    }
  }

  return Array.from(rxMap.values()).sort((a, b) => {
    const da = a.verifiedAt ? new Date(a.verifiedAt).getTime() : 0;
    const db = b.verifiedAt ? new Date(b.verifiedAt).getTime() : 0;
    return db - da;
  });
}
